/**
 * OnboardingLayout (T-032).
 *
 * Onboarding's OWN focused layout — deliberately NOT the portal kanban shell
 * (Phase G owns PortalLayout). Per app/docs/aldente/ui-structure.md
 * §Layouts > OnboardingLayout and design-guidelines.md §5:
 *   - Header: minimal, logo only, no sign-in link.
 *   - Footer: omitted.
 *   - Container: max-w-2xl, centered, step indicator at top.
 *   - Background: linen (DES-01).
 *
 * Tone for the surface is the client-communication register: warmer, attentive
 * (design-guidelines.md §"Tone defaults per surface"). Copy lives in the step
 * components; the layout is chrome only.
 *
 * The step indicator is omitted on the holding page (no step there); pass
 * `step` only on the flow itself.
 */

import type { ReactNode } from 'react';

interface OnboardingLayoutProps {
  children: ReactNode;
  /** 1..6 — renders the step indicator. Omit on the submitted holding page. */
  step?: number;
  totalSteps?: number;
}

export function OnboardingLayout({
  children,
  step,
  totalSteps = 6,
}: OnboardingLayoutProps) {
  const pct =
    typeof step === 'number'
      ? `${Math.round((Math.min(Math.max(step, 1), totalSteps) / totalSteps) * 100)}%`
      : '0%';
  return (
    <div className="min-h-screen bg-[color:var(--color-white)] text-[color:var(--color-navy)]">
      {/* Continuous progress rail (prototype Shell signature). The accessible
          step count lives in StepIndicator below; this bar is decorative. */}
      {typeof step === 'number' && (
        <div
          className="h-[3px] w-full bg-[color:var(--color-rule)]"
          aria-hidden="true"
        >
          <div
            className="h-full bg-[color:var(--color-navy)] transition-[width] duration-slow ease-out"
            style={{ width: pct }}
          />
        </div>
      )}

      <header
        role="banner"
        className="flex items-center border-b border-[color:var(--color-rule)] px-4 py-4 sm:px-6"
      >
        <span className="font-display text-lg italic text-[color:var(--color-navy)]">
          Yes Please Studio
        </span>
      </header>

      <main className="px-4 py-12 sm:px-6 sm:py-16">
        <div className="mx-auto max-w-2xl">
          {typeof step === 'number' && (
            <StepIndicator step={step} totalSteps={totalSteps} />
          )}
          <div className="mt-8 rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-6 shadow-[var(--shadow-sm)] sm:p-10">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * Step indicator: "Step N of 6" with a row of segment bars. The current and
 * completed steps use the foreground tone; future steps are muted linen
 * (design-guidelines.md §timeline: "Future steps use --color-linen (muted)").
 * The text label is present for screen readers, not color alone (DES-09).
 */
function StepIndicator({
  step,
  totalSteps,
}: {
  step: number;
  totalSteps: number;
}) {
  const clamped = Math.min(Math.max(step, 1), totalSteps);
  return (
    <div>
      <p
        className="text-sm font-medium text-[color:var(--color-text-mute)]"
        aria-live="polite"
      >
        Step {clamped} of {totalSteps}
      </p>
      <ol
        className="mt-3 flex gap-1.5"
        aria-label={`Onboarding progress: step ${clamped} of ${totalSteps}`}
      >
        {Array.from({ length: totalSteps }, (_, i) => {
          const n = i + 1;
          const done = n < clamped;
          const current = n === clamped;
          return (
            <li
              key={n}
              aria-current={current ? 'step' : undefined}
              className={[
                'h-1.5 flex-1 rounded-full',
                done
                  ? 'bg-[color:var(--color-navy)]'
                  : current
                    ? 'bg-[color:var(--color-rose)]'
                    : 'bg-[color:var(--color-linen-deep)] ring-1 ring-inset ring-[color:var(--color-rule)]',
              ].join(' ')}
            >
              <span className="sr-only">
                {done
                  ? `Step ${n} complete`
                  : current
                    ? `Step ${n}, current`
                    : `Step ${n}, not started`}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
